import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, DollarSign, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { PriceCheckResponse } from '@/api/types';

interface PriceCheckSectionProps {
  readonly data: PriceCheckResponse;
  readonly onRecheck: () => void;
  readonly isRechecking: boolean;
  readonly onApplyPrice: (description: string, price: number) => void;
  readonly onApplyAll: (suggestions: Array<{ description: string; price: number }>) => void;
}

function getPositionColor(position: string): string {
  if (position === 'competitive')
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  if (position === 'below')
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
}

function getMidpoint(low: number, high: number): number {
  return (low + high) / 2;
}

export function PriceCheckSection({
  data,
  onRecheck,
  isRechecking,
  onApplyPrice,
  onApplyAll,
}: Readonly<PriceCheckSectionProps>) {
  const { t } = useTranslation();
  const [appliedItems, setAppliedItems] = useState<Set<string>>(new Set());

  const handleApply = (description: string, low: number, high: number) => {
    const midpoint = getMidpoint(low, high);
    setAppliedItems((prev) => new Set(prev).add(description));
    onApplyPrice(description, midpoint);
  };

  const handleApplyAll = () => {
    const unapplied = data.item_suggestions.filter((item) => !appliedItems.has(item.description));
    const allDescriptions = new Set(data.item_suggestions.map((item) => item.description));
    setAppliedItems(allDescriptions);
    onApplyAll(
      unapplied.map((item) => ({
        description: item.description,
        price: getMidpoint(item.suggested_low, item.suggested_high),
      })),
    );
  };

  const allApplied =
    data.item_suggestions.length > 0 &&
    data.item_suggestions.every((item) => appliedItems.has(item.description));

  return (
    <div data-testid="price-check-section" className="mt-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          <h3 className="text-base font-semibold">{t('priceCheck.title')}</h3>
        </div>
        <Button
          data-testid="price-check-recheck-btn"
          variant="ghost"
          size="sm"
          onClick={onRecheck}
          disabled={isRechecking}
          className="min-h-[44px]"
        >
          {isRechecking ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-1">{t('priceCheck.recheck')}</span>
        </Button>
      </div>

      {/* Overall assessment */}
      <p data-testid="price-check-assessment" className="text-muted-foreground text-sm">
        {data.overall_assessment}
      </p>

      {/* Item suggestions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            <span>{t('priceCheck.itemSuggestions')}</span>
            {data.item_suggestions.length > 1 && (
              <Button
                data-testid="price-check-apply-all-btn"
                variant="outline"
                size="sm"
                onClick={handleApplyAll}
                disabled={allApplied}
                className="min-h-[44px]"
              >
                {t('priceCheck.applyAll')}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.item_suggestions.length === 0 ? (
            <p data-testid="price-check-empty" className="text-muted-foreground text-sm">
              {t('priceCheck.noSuggestions')}
            </p>
          ) : (
            data.item_suggestions.map((item) => {
              const isApplied = appliedItems.has(item.description);
              return (
                <div
                  key={item.description}
                  data-testid="price-check-item"
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="flex-1 space-y-0.5">
                    <div className="font-medium">{item.description}</div>
                    <div className="text-muted-foreground text-xs">
                      {t('priceCheck.yourPrice')}: €{item.current_price.toFixed(2)}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('priceCheck.marketRange')}: €{item.suggested_low.toFixed(2)} – €
                      {item.suggested_high.toFixed(2)}
                    </div>
                    <Badge
                      data-testid="price-check-position-badge"
                      className={getPositionColor(item.market_position)}
                    >
                      {t(`priceCheck.${item.market_position}`)}
                    </Badge>
                  </div>
                  {isApplied ? (
                    <Button
                      data-testid="price-check-applied-btn"
                      variant="secondary"
                      size="sm"
                      disabled
                      className="min-h-[44px]"
                    >
                      <Check className="h-4 w-4" />
                      <span className="ml-1">{t('priceCheck.applied')}</span>
                    </Button>
                  ) : (
                    <Button
                      data-testid="price-check-apply-btn"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleApply(item.description, item.suggested_low, item.suggested_high)
                      }
                      className="min-h-[44px]"
                    >
                      {t('priceCheck.apply')}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
